/* eslint-disable no-prototype-builtins */
/* eslint-disable no-invalid-this */
/**
 * Convert and export the current dataset as a JSON object.
 * @param {string} outputFormat - The format of the series output ("object" or "array"). Defaults to "object" for backward compatibility.
 * @returns {Object} The exported data.
 */
export function exportData(outputFormat = 'object') {
	const isIntraday = this.state && this.state.isIntraday;
	const symbolData = (this.data && this.data[this.params.symbol]) ? this.data[this.params.symbol] : {};
	const series = outputFormat === 'array' ? [] : {};
	const columns = [];

	if (typeof symbolData === 'object' && Object.keys(symbolData).length > 0) {
		Object.keys(symbolData).forEach(datasetEntry => {
			const dataset = this.getFirstDataset(symbolData[datasetEntry]);
			for (const item of dataset) {
				const dateObj = new Date(item.date);
				const date = isIntraday ? dateObj.toLocaleString() : dateObj.toLocaleDateString();

				if (outputFormat === 'object') {
					series[date] = series[date] || {};
				}

				for (const key in item) {
					if (item.hasOwnProperty(key) && !/date|diff|hollow/.test(key)) {
						if (outputFormat === 'object') {
							series[date][key] = series[date][key] || item[key];
						} else {
							series.push({ date, ...item });
						}

						if (!columns.includes(key)) {
							columns.push(key);
						}
					}
				}
			}
		});
	} else {
		console.warn('Symbol data not found or invalid format.');
	}

	return {
		series,
		columns
	};
}

/**
 * Convert and export the complete dataset as a JSON object, including symbols, indicators, comparisons, and normalized values.
 * Repeated metadata (like indicator descriptions) is pulled into a separate metadata section to reduce redundancy.
 * Indicator values are grouped by date under 'series'.
 * The date is formatted in UTC ISO format.
 * @returns {Object} The exported data.
 */
export function exportAllData() {
	const exportDataOut = {
		metadata: {
			indicators: {}
		},
		symbols: []
	};

	Object.keys(this.data).forEach(xid => {
		const symbolData = this.data[xid];
		const symbolXref = this.xref[xid];

		const symbolExport = {
			xid,
			companyName: symbolXref ? symbolXref.companyName : undefined,
			displaySymbol: symbolXref ? symbolXref.displaySymbol : undefined,
			currency: symbolXref ? symbolXref.currency : undefined,
			exchangeId: symbolXref ? symbolXref.exchangeId : undefined,
			utcOffset: symbolXref ? symbolXref.utcOffset : undefined,
			series: {}
		};

		Object.keys(symbolData).forEach(datasetType => {
			const datasetEntries = symbolData[datasetType];

			Object.keys(datasetEntries).forEach(uid => {
				const dataset = datasetEntries[uid];
				const indicatorParams = this.getIndicatorByUid(uid);

				if (datasetType !== 'price' && !exportDataOut.metadata.indicators[uid]) {
					exportDataOut.metadata.indicators[uid] = {
						uid,
						type: datasetType,
						name: indicatorParams ? indicatorParams.params.name : datasetType,
						description: indicatorParams ? indicatorParams.params.description : ''
					};
				}

				dataset.forEach(item => {
					const dateObj = item.date;
					const formattedDate = new Date(dateObj).toISOString();

					if (!symbolExport.series[formattedDate]) {
						symbolExport.series[formattedDate] = { price: {}, indicators: {} };
					}

					if (datasetType === 'price') {
						symbolExport.series[formattedDate].price = {
							open: item.open,
							high: item.high,
							low: item.low,
							close: item.close
						};

						if (this.panels[0].isNormalized && this.state.normalizeValues?.indicators && this.state.normalizeValues.indicators[uid]) {
							const normalizedValues = this.state.normalizeValues.indicators[uid];
							symbolExport.series[formattedDate].price.actual = {
								open: this.getActualFromNormalized(item.open, normalizedValues.open),
								high: this.getActualFromNormalized(item.high, normalizedValues.high),
								low: this.getActualFromNormalized(item.low, normalizedValues.low),
								close: this.getActualFromNormalized(item.close, normalizedValues.close)
							};
						}
					} else {
						const valueObj = {};
						const valueKeys = Object.keys(item).filter(key => !/date|dateIndex|diff|hollow/.test(key));
						valueKeys.forEach(key => {
							valueObj[key] = item[key];
						});
						symbolExport.series[formattedDate].indicators[uid] = valueObj;
					}
				});
			});
		});

		exportDataOut.symbols.push(symbolExport);
	});

	return exportDataOut;
}
